import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/he";
import {
  RECENT_CATEGORY_LABEL_HE,
  RECENT_SEARCH_CATEGORIES,
} from "./agentRecentSearchPayload";

dayjs.extend(customParseFormat);

const ORG_TAB = "OrganizedTour";

function normStr(v) {
  if (v == null) return "";
  return String(v).trim().toLowerCase();
}

function parseRawPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * @param {Record<string, unknown>} row — שורה מ־GET recent-searches (אופציונלי raw_payload אם יתווסף בעתיד)
 */
export function isOrganizedTourRecentSearchRow(row) {
  if (!row || typeof row !== "object") return false;
  const cat = normStr(row.category);
  if (
    cat === RECENT_SEARCH_CATEGORIES.ORGANIZED_TOURS ||
    cat === "organized_tours"
  ) {
    return true;
  }

  const st = normStr(row.searchType);
  const pt = normStr(row.productType);
  const ty = normStr(row.type);
  if (
    st.includes("organized") ||
    pt.includes("organized") ||
    ty.includes("organized")
  ) {
    return true;
  }

  const label = String(row.category_label || row.search_type_label || "").trim();
  if (label === RECENT_CATEGORY_LABEL_HE.organized_tours) return true;
  if (label.includes("מאורגנים")) return true;

  const raw = parseRawPayload(row.raw_payload);
  if (normStr(raw?.searchTab) === normStr(ORG_TAB)) return true;

  return false;
}

function formatDdMmFromIso(iso) {
  if (!iso) return null;
  const d = dayjs(String(iso).slice(0, 10), "YYYY-MM-DD", true);
  return d.isValid() ? d.format("DD/MM/YYYY") : String(iso);
}

function legacyDateRangeCell(row) {
  const a = formatDdMmFromIso(row.departure_date);
  const b = formatDdMmFromIso(row.return_date);
  if (!a && !b) return "—";
  const parts = [a, b].filter(Boolean);
  let s = parts.length === 2 ? `${parts[0]} – ${parts[1]}` : parts[0] || "—";
  const n = row.nights;
  if (n != null && n !== "" && !Number.isNaN(Number(n))) {
    s += ` · ${Number(n)} לילות`;
  }
  return s;
}

function tryParseMonthYearToken(token) {
  if (token == null) return null;
  const s = String(token).trim();
  if (!s) return null;
  const mmYyyy = dayjs(s, "MM/YYYY", true);
  if (mmYyyy.isValid()) return mmYyyy.locale("he");
  return null;
}

function tryParseAnyDate(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const parsers = [
    () => dayjs(s.slice(0, 10), "YYYY-MM-DD", true),
    () => dayjs(s, "YYYY-MM-DD", true),
    () => dayjs(s, "DD/MM/YYYY", true),
    () => dayjs(s, "MM/YYYY", true),
  ];
  for (const p of parsers) {
    const d = p();
    if (d && d.isValid()) return d.locale("he");
  }
  return null;
}

function resolveOrganizedTourMonthDayjs(row, raw) {
  const rawSafe = raw && typeof raw === "object" ? raw : {};
  const explicit = [
    rawSafe.tourMonth,
    rawSafe.organizedTourMonth,
    rawSafe.monthYear,
    rawSafe.departureMonth,
  ];
  for (const ex of explicit) {
    const m = tryParseMonthYearToken(ex);
    if (m) return { d: m, reason: "raw_explicit_month" };
  }

  if (
    rawSafe.month != null &&
    rawSafe.year != null &&
    !Number.isNaN(Number(rawSafe.year)) &&
    !Number.isNaN(Number(rawSafe.month))
  ) {
    const yi = Number(rawSafe.year);
    const mi = Number(rawSafe.month);
    if (yi >= 1970 && yi <= 2100 && mi >= 1 && mi <= 12) {
      const m = dayjs(new Date(yi, mi - 1, 1));
      if (m.isValid()) return { d: m.locale("he"), reason: "raw_month_year_ints" };
    }
  }

  const fromRowDep = tryParseAnyDate(row?.departure_date);
  if (fromRowDep) {
    return { d: fromRowDep.startOf("month"), reason: "row_departure_date" };
  }

  const calArr = rawSafe.calendarDateArrival;
  if (calArr != null && String(calArr).includes("/")) {
    const mm = tryParseMonthYearToken(calArr);
    if (mm) return { d: mm, reason: "raw_calendar_mm_yyyy" };
    const any = tryParseAnyDate(calArr);
    if (any) return { d: any.startOf("month"), reason: "raw_calendar_other" };
  }

  const dateFields = [
    rawSafe.departureDate,
    rawSafe.fromDate,
    rawSafe.startDate,
  ];
  for (const f of dateFields) {
    const d = tryParseAnyDate(f);
    if (d) return { d: d.startOf("month"), reason: "raw_date_field" };
  }

  return { d: null, reason: "no_date" };
}

function formatHebrewMonthYear(d) {
  return d.locale("he").format("MMMM YYYY");
}

/**
 * תצוגת עמודת תאריכים בטבלת חיפושים אחרונים (מאורגנים → חודש בעברית; אחרת → מחרוזת legacy).
 *
 * @param {Record<string, unknown>} row
 * @returns {string}
 */
export function formatRecentSearchDates(row) {
  const raw = parseRawPayload(row?.raw_payload);
  const isOrg = isOrganizedTourRecentSearchRow(row);

  if (isOrg) {
    const { d, reason } = resolveOrganizedTourMonthDayjs(row, raw || {});
    const resolvedDisplay = d && d.isValid() ? formatHebrewMonthYear(d) : "—";
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.info("[RECENT_SEARCHES_DATES_TRACE]", {
        category: row?.category ?? null,
        searchType: row?.searchType ?? null,
        productType: row?.productType ?? null,
        rawDates: {
          departure_date: row?.departure_date ?? null,
          return_date: row?.return_date ?? null,
          raw_payload: raw ?? null,
        },
        resolvedDisplay,
        reason,
      });
    }
    return resolvedDisplay;
  }

  const legacy = legacyDateRangeCell(row);
  return legacy;
}
